import { TABLE_TOP_CORNERS } from "../../v2/components/tableHeaderCorners.js";
// src/features/reports/SpaceFitReport.jsx
//
// SPACE-FIT-REPORT-S2-POPULATE: Space Fit report — live KPI row, sortable
// table, and quadrant scatter chart.
//
// Data source: getSpaceFitReport() from reportsClient.js which calls
//   getLocations() client-side and maps through computeSpaceFit().
//   No new DB queries or migrations.
//
// Views: Table (default) | Chart (segmented control toggle)
// Feature flag: report-space-fit  Role: viewer+

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { T } from "../../app/tokens.js";
import { OverflowTable } from "../../v2/components/ListOverflow.jsx";
import { useChartHoverIntent } from "../../components/useChartHoverIntent.js";
import { KpiBand } from "../../components/KpiBand.jsx";
import { KpiTile } from "../../components/KpiTile.jsx";
import { ReportShell } from "./ReportShell.jsx";
import { SearchInput } from "../../v2/components/SearchInput.jsx";
import { ConsolidatedFilter } from "../../v2/components/ConsolidatedFilter.jsx";
import { SortControl } from "../../v2/components/SortControl.jsx";
import { fmtSqft } from "../../utils/format.js";
import {
  SPACE_FIT_VERDICT_OVER,
  SPACE_FIT_VERDICT_DENSE,
  SPACE_FIT_VERDICT_UNDER,
  SPACE_FIT_VERDICT_RIGHT,
  SPACE_FIT_SEVERITY,
  SPACE_FIT_LABEL,
  SPACE_FIT_BG,
  SPACE_FIT_COLOR,
  SPACE_FIT_BORDER,
  SPACE_FIT_HEX,
  DESK_BALANCED_MIN,
  DESK_OVER_THRESHOLD,
} from "../../utils/spaceFitVerdict.js";
import {
  COMFORT_TIGHT_MIN_SF_PER_SEAT,
  COMFORT_ROOMY_MIN_SF_PER_SEAT,
} from "../../utils/comfortDensity.js";
import { getSpaceFitReport } from "../../data/reportsClient.js";
import { LEASE_TYPES, LEASE_DISPLAY } from "../../utils/leaseTypeConstants.js";
import { useReportViewState } from "../../v2/hooks/useReportViewState.js";
import { REPORT_SPACE_FIT_KEY } from "../../services/portfolioStatePersistence.js";
import { matchesFoldedQuery } from "../../utils/textSearch.js";
import { useFeatureFlagContext } from "../../contexts/FeatureFlagContext.jsx";
import { getReportNavEligibility } from "./reportNavEligibility.js";
import { ReportAccessNotice } from "./ReportShell.jsx";

// ─── Constants ────────────────────────────────────────────────────────────────

const DENSITY_COMFORTABLE = "comfortable";
const DENSITY_COMPACT = "compact";

// Field descriptors for useReportViewState — defines URL params, valid values,
// and defaults for all display state in SpaceFitReport.
const SF_VIEW_STATE_FIELDS = [
  { param: "view",      valid: ["table", "chart"],                                         default: "table"       },
  { param: "density",   valid: ["comfortable", "compact"],                                 default: "comfortable" },
  { param: "q",                                                                             default: ""            },
  { param: "sort",      valid: ["verdict", "name", "sf", "seats", "people", "sfPerSeat"],  default: "verdict"     },
  { param: "dir",       valid: ["asc", "desc"],                                             default: "asc"         },
  { param: "region",    array: true,                                                        default: []            },
  { param: "verdict",   array: true,                                                        default: []            },
  { param: "hub",       array: true,                                                        default: []            },
  { param: "leaseType", array: true,                                                        default: []            },
];

// Display labels and colors for the densityBand from computeSpaceFit.
// densityBand output: "roomy" | "comfortable" | "cramped" | null
const DENSITY_DISPLAY = {
  roomy:       { label: "Roomy",   bg: "var(--v2-green-tint)", color: "var(--v2-green)" },
  comfortable: { label: "Tight",   bg: "var(--v2-amber-tint)", color: T.ragAmberText },
  cramped:     { label: "Crowded", bg: "var(--v2-red-tint)",   color: "var(--v2-red)" },
};

// KPI tile definitions — order and colors per mockup.
const KPI_DEFS = [
  { key: "assessed", label: "Offices assessed",                        foot: "with a verdict",         numColor: T.blue },
  { key: "over",     label: SPACE_FIT_LABEL[SPACE_FIT_VERDICT_OVER].full,  foot: "more people than desks", numColor: "var(--v2-red)" },
  { key: "dense",    label: SPACE_FIT_LABEL[SPACE_FIT_VERDICT_DENSE].full, foot: "tight build, seated ok", numColor: T.ragAmberText },
  { key: "under",    label: SPACE_FIT_LABEL[SPACE_FIT_VERDICT_UNDER].full, foot: "empty desks",            numColor: T.ragAmberText },
  { key: "right",    label: SPACE_FIT_LABEL[SPACE_FIT_VERDICT_RIGHT].full, foot: "comfortable + subscribed", numColor: "var(--v2-green)" },
];

// Sort fields for the SortControl dropdown.
const SF_SORT_FIELDS = [
  { value: "verdict",   label: "Space Fit verdict", numeric: false },
  { value: "name",      label: "Office name",        numeric: false },
  { value: "sf",        label: "Sq ft",              numeric: true  },
  { value: "seats",     label: "Seats",              numeric: true  },
  { value: "people",    label: "People",             numeric: true  },
  { value: "sfPerSeat", label: "SF / seat",          numeric: true  },
];

// ─── Sort helpers ─────────────────────────────────────────────────────────────

function verdictSeverity(v) {
  return v != null ? (SPACE_FIT_SEVERITY[v] ?? 99) : 99;
}

function sortOffices(offices, key, dir, marketsEnabled = true) {
  const mult = dir === "asc" ? 1 : -1;
  return [...offices].sort((a, b) => {
    if (key === "verdict") {
      const sv = verdictSeverity(a.verdict) - verdictSeverity(b.verdict);
      if (sv !== 0) return sv; // severity order is always asc (worst first)
      // Secondary: SF desc
      const sa = a.sf ?? -Infinity;
      const sb = b.sf ?? -Infinity;
      return sb - sa;
    }
    if (key === "name") {
      const na = marketsEnabled
        ? `${a.market} ${a.locationAlias ?? ""}`.trim().toLowerCase()
        : (a.locationAlias || a.locationId || "").toLowerCase();
      const nb = marketsEnabled
        ? `${b.market} ${b.locationAlias ?? ""}`.trim().toLowerCase()
        : (b.locationAlias || b.locationId || "").toLowerCase();
      return mult * na.localeCompare(nb);
    }
    // Numeric fields — nulls always last
    const va = a[key] ?? (dir === "asc" ? Infinity : -Infinity);
    const vb = b[key] ?? (dir === "asc" ? Infinity : -Infinity);
    return mult * (va - vb);
  });
}

// ─── Filter helpers ───────────────────────────────────────────────────────────

function filterOffices(offices, search, selections, marketsEnabled = true) {
  const q = search.trim();
  const regions    = marketsEnabled ? selections.region ?? [] : [];
  const verdicts   = selections.verdict   ?? [];
  const hubs       = selections.hub       ?? [];
  const leaseTypes = selections.leaseType ?? [];
  return offices.filter((o) => {
    if (regions.length  && !regions.includes(o.region))   return false;
    if (verdicts.length && !verdicts.includes(o.verdict ?? "—")) return false;
    if (hubs.length) {
      const isHub = o.isHub === true;
      const matchesHub = (hubs.includes("Hub") && isHub) || (hubs.includes("Non-Hub") && !isHub);
      if (!matchesHub) return false;
    }
    if (leaseTypes.length) {
      const types = o.activeLeaseTypes ?? [];
      if (types.length === 0) {
        if (!leaseTypes.includes("__none__")) return false;
      } else if (!types.some((t) => leaseTypes.includes(t))) return false;
    }
    if (q) {
      if (!matchesFoldedQuery(q, marketsEnabled ? o.market : "", o.locationAlias, o.locationId)) return false;
    }
    return true;
  });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function VerdictPill({ verdict }) {
  if (!verdict) return <span style={{ color: T.mid }}>—</span>;
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      fontSize: T.fsXs,
      fontWeight: T.fwSemibold,
      padding: "2px 10px",
      borderRadius: 6,
      whiteSpace: "nowrap",
      background: SPACE_FIT_BG[verdict],
      color: SPACE_FIT_COLOR[verdict],
    }}>
      {SPACE_FIT_LABEL[verdict]?.full ?? verdict}
    </span>
  );
}

function DensityPill({ band }) {
  const info = band ? DENSITY_DISPLAY[band] : null;
  if (!info) return null;
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      fontSize: T.fsXs,
      fontWeight: T.fwSemibold,
      padding: "2px 10px",
      borderRadius: 6,
      background: info.bg,
      color: info.color,
      marginLeft: 5,
      flexShrink: 0,
    }}>
      {info.label}
    </span>
  );
}

function SpacePerSeatCell({ sf, seats, densityBand }) {
  if (sf == null || seats == null || seats === 0) {
    return <span style={{ color: T.mid }}>—</span>;
  }
  const ratio = Math.round(sf / seats);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
      <span>≈{ratio.toLocaleString()}</span>
      <DensityPill band={densityBand} />
    </span>
  );
}

function DeskFitCell({ people, seats, deskDelta }) {
  if (seats == null) return <span style={{ color: T.mid }}>—</span>;
  if (people == null) return <span style={{ color: T.mid }}>—</span>;
  // displayDelta = seats - people (positive = surplus seats, negative = overflow)
  const displayDelta = -deskDelta;
  const sign = displayDelta > 0 ? "+" : "";
  const deltaColor = displayDelta > 0 ? "var(--v2-green)" : displayDelta < 0 ? "var(--v2-red)" : T.mid;
  return (
    <span>
      {people}/{seats}{" "}
      <span style={{ color: deltaColor, fontWeight: T.fwBold }}>
        {sign}{displayDelta}
      </span>
    </span>
  );
}

function CompactDensitySwitch({ checked, onChange, testId = "sf-density-toggle" }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      onClick={onChange}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 9,
        fontSize: T.fsSm5,
        fontWeight: T.fwBold,
        color: checked ? T.accent : T.mid,
        cursor: "pointer",
        userSelect: "none",
        background: "none",
        border: "none",
        padding: 0,
      }}
    >
      <span style={{
        position: "relative",
        width: 34,
        height: 20,
        borderRadius: 999,
        background: checked ? T.accent : T.switchOff,
        transition: "background 0.15s",
        flexShrink: 0,
        display: "inline-block",
        verticalAlign: "middle",
        border: "none",
        padding: 0,
        cursor: "pointer",
      }} aria-hidden="true">
        <span style={{
          position: "absolute",
          top: 2,
          left: checked ? 16 : 2,
          width: 16,
          height: 16,
          borderRadius: "50%",
          background: T.white,
          boxShadow: "0 1px 2px rgba(16,36,58,0.3)",
          transition: "left 0.15s",
          pointerEvents: "none",
        }} />
      </span>
      Compact
    </button>
  );
}

function SegmentedControl({ value, onChange }) {
  const btn = (v, label) => (
    <button
      key={v}
      type="button"
      data-testid={`sf-view-${v}`}
      onClick={() => onChange(v)}
      style={{
        fontSize: T.fsSm5,
        fontWeight: T.fwBold,
        padding: "5px 14px",
        borderRadius: 6,
        border: "none",
        cursor: "pointer",
        background: value === v ? T.card : "transparent",
        color: value === v ? T.dark : T.mid,
        boxShadow: value === v ? "0 1px 2px rgba(15,23,42,.08)" : "none",
        transition: "all 0.12s",
      }}
    >
      {label}
    </button>
  );
  return (
    <div style={{
      display: "inline-flex",
      background: T.lightGray,
      border: `1px solid ${T.border}`,
      borderRadius: 8,
      padding: 2,
    }}>
      {btn("table", "Table")}
      {btn("chart", "Chart")}
    </div>
  );
}

// ─── Scatter Chart ────────────────────────────────────────────────────────────

// Verdict hex colors for SVG — uses SPACE_FIT_HEX from spaceFitVerdict.js
// (T token-backed values, suitable for SVG fill attributes).
const VERDICT_HEX = SPACE_FIT_HEX;

// Chart layout constants
const CHART_W    = 760;
const CHART_H    = 340;
const PLOT_L     = 70;
const PLOT_R     = 740;
const PLOT_T     = 20;
const PLOT_B     = 295;
const PLOT_W     = PLOT_R - PLOT_L;
const PLOT_H     = PLOT_B - PLOT_T;
const X_MAX_PCT  = 160; // max % desk demand shown
const Y_MAX_SF   = 750; // max SF/seat shown

function xScale(pct)  { return PLOT_L + (Math.min(pct, X_MAX_PCT) / X_MAX_PCT) * PLOT_W; }
function yScale(sfps) { return PLOT_B - (Math.min(sfps, Y_MAX_SF) / Y_MAX_SF) * PLOT_H; }

export function SpaceFitScatterChart({ offices, marketsEnabled = true }) {
  const plotOffices = useMemo(() =>
    offices.filter((o) => o.sf != null && o.seats != null && o.people != null),
    [offices]);

  const intent = useChartHoverIntent(JSON.stringify([offices, marketsEnabled]));
  const hovered = intent.open ? intent.point : null;
  const containerRef = useRef(null);

  const xBalanced = xScale(DESK_BALANCED_MIN * 100);
  const x100 = xScale(DESK_OVER_THRESHOLD * 100);
  const y150 = yScale(COMFORT_TIGHT_MIN_SF_PER_SEAT);
  const y250 = yScale(COMFORT_ROOMY_MIN_SF_PER_SEAT);

  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: "16px 18px" }}>
      <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark, marginBottom: 2 }}>
        Where every office sits
      </div>
      <div style={{ fontSize: T.fsSm5, color: T.mid, marginBottom: 14 }}>
        Desk demand (→ right = more subscribed) vs. build density (↑ up = more SF per seat).
        Right of the red line = over on desks. Below the lower dashes = crowded build.
      </div>

      <div {...intent.containerProps} ref={containerRef} style={{ position: "relative" }}>
        <svg
          data-testid="sf-scatter-chart"
          viewBox={`0 0 ${CHART_W} ${CHART_H}`}
          style={{ width: "100%", height: "auto", display: "block" }}
          aria-label="Space Fit quadrant scatter chart"
        >
          {/* Axes */}
          <line x1={PLOT_L} y1={PLOT_T} x2={PLOT_L} y2={PLOT_B} stroke={T.border} strokeWidth={1.5} />
          <line x1={PLOT_L} y1={PLOT_B} x2={PLOT_R} y2={PLOT_B} stroke={T.border} strokeWidth={1.5} />

          {/* Y axis tick labels */}
          {[0, 150, 250, 400, 600, 750].map((v) => (
            <g key={v}>
              <line x1={PLOT_L - 4} y1={yScale(v)} x2={PLOT_L} y2={yScale(v)} stroke={T.border} strokeWidth={1} />
              <text x={PLOT_L - 7} y={yScale(v) + 4} textAnchor="end" fontSize={9} fill={T.mid}>{v}</text>
            </g>
          ))}

          {/* X axis tick labels */}
          {[0, 50, 100, 150].map((v) => (
            <g key={v}>
              <line x1={xScale(v)} y1={PLOT_B} x2={xScale(v)} y2={PLOT_B + 4} stroke={T.border} strokeWidth={1} />
              <text x={xScale(v)} y={PLOT_B + 14} textAnchor="middle" fontSize={9} fill={T.mid}>{v}%</text>
            </g>
          ))}

          {/* Y axis label */}
          <text
            x={12}
            y={(PLOT_T + PLOT_B) / 2}
            textAnchor="middle"
            fontSize={9}
            fill={T.mid}
            transform={`rotate(-90, 12, ${(PLOT_T + PLOT_B) / 2})`}
          >
            SF / seat  →  spacious
          </text>

          {/* X axis label */}
          <text x={(PLOT_L + PLOT_R) / 2} y={CHART_H - 2} textAnchor="middle" fontSize={9} fill={T.mid}>
            Desk demand %  (under-subscribed ← 100% → over-subscribed)
          </text>

          {/* Threshold: horizontal dashed at 250 SF/seat */}
          <line x1={PLOT_L} y1={y250} x2={PLOT_R} y2={y250} stroke={T.border} strokeWidth={1} strokeDasharray="4 3" />
          <text x={PLOT_L + 4} y={y250 - 3} fontSize={9} fill={T.mid}>250 · Roomy</text>

          {/* Threshold: horizontal dashed at 150 SF/seat */}
          <line x1={PLOT_L} y1={y150} x2={PLOT_R} y2={y150} stroke={T.border} strokeWidth={1} strokeDasharray="4 3" />
          <text x={PLOT_L + 4} y={y150 - 3} fontSize={9} fill={T.mid}>150 · Crowded below</text>

          {/* Threshold: vertical dashed at balanced desk demand minimum */}
          <line
            data-testid="sf-balanced-line"
            x1={xBalanced} y1={PLOT_T} x2={xBalanced} y2={PLOT_B}
            stroke={T.border} strokeWidth={1} strokeDasharray="4 3"
          />
          <text x={xBalanced + 4} y={PLOT_T + 11} fontSize={9} fill={T.mid}>
            {DESK_BALANCED_MIN * 100}% desks
          </text>

          {/* Threshold: vertical solid at 100% desk demand */}
          <line
            x1={x100} y1={PLOT_T} x2={x100} y2={PLOT_B}
            stroke="rgba(194,16,46,0.4)" strokeWidth={1.5} strokeDasharray="5 3"
          />
          <text x={x100 + 4} y={PLOT_T + 11} fontSize={9} fill="rgba(194,16,46,0.6)">
            100% desks
          </text>

          {/* Target zone: balanced desk demand × 150–250 SF/seat */}
          <rect
            data-testid="sf-target-zone"
            x={xBalanced}
            y={y250}
            width={x100 - xBalanced}
            height={y150 - y250}
            fill="rgba(31,157,107,0.10)"
            stroke="rgba(31,157,107,0.55)"
            strokeWidth={1.3}
            rx={3}
          />
          <text
            x={(xBalanced + x100) / 2}
            y={y250 - 10}
            textAnchor="middle"
            fontSize={8}
            fontWeight="bold"
            fill="rgba(31,157,107,0.80)"
            letterSpacing={0.8}
          >
            TARGET ZONE
          </text>
          <text
            x={(xBalanced + x100) / 2}
            y={y250 - 2}
            textAnchor="middle"
            fontSize={7}
            fill="rgba(31,157,107,0.65)"
          >
            {DESK_BALANCED_MIN * 100}–100% desks
          </text>

          {/* Data dots */}
          {plotOffices.map((o) => {
            const xPct = (o.people / o.seats) * 100;
            const yVal = o.sf / o.seats;
            const cx = xScale(xPct);
            const cy = yScale(yVal);
            const color = o.verdict ? VERDICT_HEX[o.verdict] : T.light;

            return (
              <g key={o.id} data-testid={`sf-dot-${o.id}`}>
                <circle
                  {...intent.pointProps({ office: o, x: cx / CHART_W * (containerRef.current?.offsetWidth ?? CHART_W), y: cy / CHART_W * (containerRef.current?.offsetWidth ?? CHART_W) })}
                  aria-label={o.locationAlias || o.market || o.id}
                  cx={cx}
                  cy={cy}
                  r={6}
                  fill={color}
                  stroke="white"
                  strokeWidth={1.5}
                  style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.15))", cursor: "pointer" }}
                  onMouseEnter={(e) => {
                    if (!containerRef.current) return;
                    const cr = e.currentTarget.getBoundingClientRect();
                    const pr = containerRef.current.getBoundingClientRect();
                    intent.activate({ office: o, x: cr.left - pr.left + cr.width / 2, y: cr.top - pr.top });
                  }}
                />
              </g>
            );
          })}
        </svg>

        {/* Hover tooltip */}
        {hovered && (() => {
          const o = hovered.office;
          const officeName = marketsEnabled ? o.market : (o.locationAlias || o.locationId);
          const name = o.locationAlias && marketsEnabled ? `${officeName} · ${o.locationAlias}` : officeName;
          const sfPerSeat = o.sfPerSeat != null ? Math.round(o.sfPerSeat) : null;
          const containerW = containerRef.current?.offsetWidth ?? 600;
          const flipLeft = hovered.x > containerW * 0.65;
          return (
            <div
              key="sf-chart-tooltip"
              role="tooltip"
              style={{
                position: "absolute",
                left: flipLeft ? undefined : hovered.x + 10,
                right: flipLeft ? (containerW - hovered.x + 10) : undefined,
                top: hovered.y - 8,
                transform: "translateY(-100%)",
                zIndex: 10,
                background: T.card,
                border: `1px solid ${T.border}`,
                borderRadius: 8,
                padding: "8px 10px",
                pointerEvents: "none",
                minWidth: 160,
                boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                color: T.dark,
              }}
            >
              <div style={{ fontWeight: T.fwBold, fontSize: T.fsSm5, marginBottom: 4 }}>{name}</div>
              <div style={{ fontSize: T.fsXxs, color: T.mid, lineHeight: 1.8 }}>
                {sfPerSeat != null && <div>Sq ft / seat · {sfPerSeat.toLocaleString()}</div>}
                {o.people != null && o.seats != null && (
                  <div>
                    Desk fit · {o.people} people / {o.seats} seats ·{" "}
                    {o.deskDelta != null
                      ? o.deskDelta > 0
                        ? `${o.deskDelta} short`
                        : `${-o.deskDelta} spare`
                      : "0 spare"}
                  </div>
                )}
                {o.verdict && (
                  <div style={{ marginTop: 4 }}>
                    <span style={{
                      display: "inline-flex",
                      alignItems: "center",
                      fontSize: T.fsXs,
                      fontWeight: T.fwSemibold,
                      padding: "2px 10px",
                      borderRadius: 6,
                      background: SPACE_FIT_BG[o.verdict],
                      color: SPACE_FIT_COLOR[o.verdict],
                    }}>
                      {SPACE_FIT_LABEL[o.verdict]?.full ?? o.verdict}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {plotOffices.length < offices.length && (
          <div style={{ fontSize: T.fsXs, color: T.light, marginTop: 8 }}>
            {offices.length - plotOffices.length} office{offices.length - plotOffices.length !== 1 ? "s" : ""} omitted - no rentable SF on file.
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 18, marginTop: 12, fontSize: T.fsXs, color: T.mid, flexWrap: "wrap" }}>
        {[
          SPACE_FIT_VERDICT_OVER,
          SPACE_FIT_VERDICT_DENSE,
          SPACE_FIT_VERDICT_UNDER,
          SPACE_FIT_VERDICT_RIGHT,
        ].map((v) => (
          <span key={v} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: VERDICT_HEX[v], flexShrink: 0 }} />
            {SPACE_FIT_LABEL[v].full}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── SpaceFitReport ───────────────────────────────────────────────────────────

export function SpaceFitReport({ onBack, prevReport = null, nextReport = null, onNavigateReport }) {
  const featureContext = useFeatureFlagContext();
  const eligibility = getReportNavEligibility(featureContext);
  const officesEnabled = eligibility.offices;
  const marketsEnabled = eligibility.markets;
  const [data,      setData]      = useState(null);   // { offices, kpis }
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);

  // Display state encoded in URL + persisted to localStorage.
  // URL params take precedence on mount; localStorage restores last view on
  // return navigation; defaults apply when both are absent.
  const [rvs, setRvs] = useReportViewState({
    storageKey: REPORT_SPACE_FIT_KEY,
    fields: SF_VIEW_STATE_FIELDS,
  });

  // Derive named aliases so existing render code needs minimal changes.
  const search    = rvs.q;
  const sortCol   = rvs.sort;
  const sortDir   = rvs.dir;
  const view      = rvs.view;
  const density   = rvs.density;
  const selections = {
    region:    rvs.region,
    verdict:   rvs.verdict,
    hub:       rvs.hub,
    leaseType: rvs.leaseType,
  };

  const isCompact = density === DENSITY_COMPACT;
  // userHasSetSort: derived from state; true when sort is not at its defaults.
  const userHasSetSort = sortCol !== "verdict" || sortDir !== "asc";

  useEffect(() => {
    if (!eligibility.ready || !officesEnabled) {
      setData(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getSpaceFitReport()
      .then((report) => {
        if (cancelled) return;
        setData(report);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message ?? "Failed to load Space Fit data.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [eligibility.ready, officesEnabled]);

  // ── Filter dimensions derived from loaded data ─────────────────────────────

  const filterDimensions = useMemo(() => {
    if (!data) return [];
    const offices = data.offices;

    const regionSet = new Set(offices.map((o) => o.region).filter(Boolean));
    const regionOpts = [...regionSet].sort().map((r) => ({ value: r, label: r }));

    const verdictOpts = [
      { value: SPACE_FIT_VERDICT_OVER,  label: "Over-Capacity" },
      { value: SPACE_FIT_VERDICT_DENSE, label: "Dense" },
      { value: SPACE_FIT_VERDICT_UNDER, label: "Under-Used" },
      { value: SPACE_FIT_VERDICT_RIGHT, label: "Right-Sized" },
      { value: "—",                     label: "No verdict (—)" },
    ].filter((opt) =>
      opt.value === "—"
        ? offices.some((o) => o.verdict == null)
        : offices.some((o) => o.verdict === opt.value)
    );

    const hubOpts = [
      { value: "Hub",     label: "Hub" },
      { value: "Non-Hub", label: "Non-Hub" },
    ];

    const leaseTypeOpts = [
      ...LEASE_TYPES.map((t) => ({ value: t, label: LEASE_DISPLAY[t] })),
      ...(offices.some((o) => !o.activeLeaseTypes?.length)
        ? [{ value: "__none__", label: "No active lease" }]
        : []),
    ];

    const dims = [];
    if (marketsEnabled && regionOpts.length > 1) dims.push({ id: "region",    label: "Region",     options: regionOpts });
    if (verdictOpts.length > 1) dims.push({ id: "verdict",   label: "Space Fit",  options: verdictOpts });
    dims.push({ id: "hub",       label: "Hub",        options: hubOpts });
    dims.push({ id: "leaseType", label: "Lease type", options: leaseTypeOpts });
    return dims;
  }, [data, marketsEnabled]);

  // ── Filtered + sorted offices ──────────────────────────────────────────────

  const filtered = useMemo(() => {
    if (!data) return [];
    return filterOffices(data.offices, search, selections, marketsEnabled);
  }, [data, search, selections, marketsEnabled]);

  const sorted = useMemo(() => {
    return sortOffices(filtered, sortCol, sortDir, marketsEnabled);
  }, [filtered, sortCol, sortDir, marketsEnabled]);

  // ── Sort control ───────────────────────────────────────────────────────────

  const sortControlPrimary   = { key: sortCol, dir: sortDir };
  const sortControlSecondary = { key: null, dir: "asc" };

  const isNonDefault =
    search.trim() !== "" ||
    Object.values(selections).some((a) => a?.length > 0) ||
    userHasSetSort;

  function handleSortControlChange({ primary }) {
    setRvs({ sort: primary.key, dir: primary.dir });
  }

  const handleClear = useCallback(() => {
    setRvs({
      q:         "",
      region:    [],
      verdict:   [],
      hub:       [],
      leaseType: [],
      sort:      "verdict",
      dir:       "asc",
    });
  }, [setRvs]);

  // ── Scroll-sync refs for sticky header/body split ──────────────────────────
  // Same pattern as HeadcountTrendReport.jsx.

  const sfHeaderRef = useRef(null);
  const sfBodyRef   = useRef(null);
  const handleSfBodyScroll = useCallback(() => {
    if (sfHeaderRef.current && sfBodyRef.current) {
      sfHeaderRef.current.scrollLeft = sfBodyRef.current.scrollLeft;
    }
  }, []);

  // Column widths for colgroup
  const COL_OFFICE  = 180;
  const COL_REGION  = marketsEnabled ? 70 : 0;
  const COL_SF      = 90;
  const COL_SEATS   = 65;
  const COL_PEOPLE  = 65;
  const COL_DESKFT  = isCompact ? 0 : 120;
  const COL_HROOM   = isCompact ? 0 : 90;
  const COL_SPSEAT  = isCompact ? 0 : 140;
  const COL_VERDICT = 120;
  const sfMinW = COL_OFFICE + COL_REGION + COL_SF + COL_SEATS + COL_PEOPLE +
                 COL_DESKFT + COL_HROOM + COL_SPSEAT + COL_VERDICT;

  // ── Render ─────────────────────────────────────────────────────────────────

  const kpis = data?.kpis ?? { total: 0, assessed: 0, over: 0, dense: 0, under: 0, right: 0 };

  if (!officesEnabled || !eligibility.ready) {
    return <ReportAccessNotice reportName="Space Fit" loading={!eligibility.ready} onBack={onBack} />;
  }

  return (
    <ReportShell
        recordNavigation
        onBack={onBack}
        backTestId="sf-back"
        prevReport={prevReport}
        nextReport={nextReport}
        onNavigateReport={onNavigateReport}
      >

      <div style={{ maxWidth: 1400, padding: "0 32px 48px", margin: "0 auto", width: "100%" }}>

        {/* KPI row */}
        {!loading && !error && (
          <div style={{ marginBottom: 16 }}>
          <KpiBand testId="sf-kpi-row" ariaLabel="Report metrics">
            {KPI_DEFS.map((def) => (
              <KpiTile
                key={def.key}
                label={def.label}
                value={kpis[def.key]}
                sub={def.key === "assessed" ? `of ${kpis.total} · operational + dormant` : def.foot}
                color={def.numColor}
                valueStyle={{ fontSize: T.fsXl5, fontWeight: T.fwDisplay }}
              />
            ))}
          </KpiBand>
          </div>
        )}
        {!loading && !error && (
          <div data-testid="sf-period-label" style={{ color: T.mid, fontSize: T.fsSm, margin: "-8px 0 14px" }}>
            Space Fit uses current installed seats and each office&apos;s latest available headcount snapshot.
          </div>
        )}

        {/* Toolbar */}
        <div
          data-testid="sf-toolbar"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          <SearchInput
            value={search}
            onChange={(v) => setRvs({ q: v })}
            placeholder="Search offices…"
            testId="sf-search"
          />
          <ConsolidatedFilter
            dimensions={filterDimensions}
            selections={selections}
            onChange={(newSel) => setRvs({
              region:    newSel.region    ?? [],
              verdict:   newSel.verdict   ?? [],
              hub:       newSel.hub       ?? [],
              leaseType: newSel.leaseType ?? [],
            })}
            testId="sf-filter"
          />
          <div style={{ display: view === "table" ? "block" : "none" }}>
            <SortControl
              fields={SF_SORT_FIELDS}
              primary={sortControlPrimary}
              secondary={sortControlSecondary}
              onChange={handleSortControlChange}
              testId="sf-sort"
              defaultKey="verdict"
            />
          </div>
          {isNonDefault && (
            <button
              type="button"
              data-testid="sf-clear-btn"
              onClick={handleClear}
              onMouseEnter={(e) => { e.currentTarget.style.background = T.blueSoft; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              style={{
                background: "transparent",
                border: "1px solid transparent",
                borderRadius: 8,
                color: "var(--v2-accent)",
                fontSize: T.fsBase,
                fontWeight: T.fwBold,
                cursor: "pointer",
                padding: "7px 11px",
              }}
            >
              Clear
            </button>
          )}
          <div style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
            {view === "table" && (
              <CompactDensitySwitch
                checked={isCompact}
                onChange={() => setRvs({ density: density === DENSITY_COMFORTABLE ? DENSITY_COMPACT : DENSITY_COMFORTABLE })}
                testId="sf-density-toggle"
              />
            )}
            <SegmentedControl value={view} onChange={(v) => setRvs({ view: v })} />
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div style={{ padding: "60px 0", textAlign: "center", color: T.mid, fontSize: T.fsBase }}>
            Loading…
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div style={{ padding: "40px 0", textAlign: "center", color: "var(--v2-red)", fontSize: T.fsBase }}>
            {error}
          </div>
        )}

        {/* Chart view */}
        {!loading && !error && view === "chart" && (
          <SpaceFitScatterChart offices={filtered} marketsEnabled={marketsEnabled} />
        )}

        {/* Table view */}
        {!loading && !error && view === "table" && (
          <>
            <div style={{
              background: T.card,
              border: `1px solid ${T.border}`,
              borderRadius: 12,
              ...TABLE_TOP_CORNERS,
              marginBottom: 18,
            }}>
              {/* Sticky header shell — NO overflow on this div (see HeadcountTrendReport pattern) */}
              <div style={{ position: "sticky", top: 0, zIndex: 4, background: T.lightGray, ...TABLE_TOP_CORNERS }}>
                <div ref={sfHeaderRef} style={{ overflowX: "hidden", ...TABLE_TOP_CORNERS }}>
                  <OverflowTable overflowScrollRef={sfBodyRef}
                    data-testid="sf-trend-header"
                    style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsSm5, tableLayout: "fixed", minWidth: sfMinW }}
                  >
                    <colgroup>
                      <col style={{ width: COL_OFFICE }} />
                      {marketsEnabled && <col style={{ width: COL_REGION }} />}
                      <col style={{ width: COL_SF }} />
                      <col style={{ width: COL_SEATS }} />
                      <col style={{ width: COL_PEOPLE }} />
                      {!isCompact && <col style={{ width: COL_DESKFT }} />}
                      {!isCompact && <col style={{ width: COL_HROOM }} />}
                      {!isCompact && <col style={{ width: COL_SPSEAT }} />}
                      <col style={{ width: COL_VERDICT }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th style={{ ...TH, textAlign: "left", position: "sticky", left: 0, background: T.lightGray, zIndex: 4 }}>
                          Office
                        </th>
                        {marketsEnabled && <th style={{ ...TH, textAlign: "left" }} data-testid="sf-col-region">Region</th>}
                        <th style={{ ...TH, textAlign: "right" }} data-testid="sf-col-sf">SF</th>
                        <th style={{ ...TH, textAlign: "right" }} data-testid="sf-col-seats">Seats</th>
                        <th style={{ ...TH, textAlign: "right" }} data-testid="sf-col-people">People</th>
                        {!isCompact && (
                          <th style={{ ...TH, textAlign: "right" }} data-testid="sf-col-deskfit">
                            Desk fit
                          </th>
                        )}
                        {!isCompact && (
                          <th style={{ ...TH, textAlign: "right" }} data-testid="sf-col-headroom">
                            Expansion
                          </th>
                        )}
                        {!isCompact && (
                          <th style={{ ...TH, textAlign: "left" }} data-testid="sf-col-spacefit">
                            <span title="Rentable sq ft per installed seat - build density.">SF / seat</span>
                          </th>
                        )}
                        <th style={{ ...TH, textAlign: "left" }} data-testid="sf-col-verdict">
                          Space Fit
                        </th>
                      </tr>
                    </thead>
                  </OverflowTable>
                </div>
              </div>

              {/* Scrollable body */}
              <div
                ref={sfBodyRef}
                onScroll={handleSfBodyScroll}
                style={{ overflowX: "auto", borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}
              >
                <table
                  data-testid="sf-trend-matrix"
                  style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsSm5, tableLayout: "fixed", minWidth: sfMinW }}
                >
                  <colgroup>
                    <col style={{ width: COL_OFFICE }} />
                    {marketsEnabled && <col style={{ width: COL_REGION }} />}
                    <col style={{ width: COL_SF }} />
                    <col style={{ width: COL_SEATS }} />
                    <col style={{ width: COL_PEOPLE }} />
                    {!isCompact && <col style={{ width: COL_DESKFT }} />}
                    {!isCompact && <col style={{ width: COL_HROOM }} />}
                    {!isCompact && <col style={{ width: COL_SPSEAT }} />}
                    <col style={{ width: COL_VERDICT }} />
                  </colgroup>
                  <tbody>
                    {sorted.length === 0 ? (
                      <tr>
                        <td
                          data-testid="sf-empty-state"
                          colSpan={isCompact ? 6 : 9}
                          style={{
                            padding: "64px 24px",
                            textAlign: "center",
                            color: T.mid,
                            fontSize: T.fsBase,
                          }}
                        >
                          No offices match your filters.
                        </td>
                      </tr>
                    ) : (
                      sorted.map((o, idx) => (
                        <tr
                          key={o.id}
                          data-testid={`sf-row-${o.id}`}
                          style={{
                            borderBottom: idx < sorted.length - 1 ? `1px solid ${T.border}` : "none",
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = T.hoverBg; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}
                        >
                          {/* Office — sticky left */}
                          <td style={{
                            ...TD,
                            position: "sticky",
                            left: 0,
                            background: "inherit",
                            zIndex: 1,
                            minWidth: COL_OFFICE,
                            padding: isCompact ? "7px 14px" : "10px 14px",
                          }}>
                            <span style={{ fontWeight: T.fwBold, color: T.dark }}>{marketsEnabled ? o.market : (o.locationAlias || o.locationId)}</span>
                            {o.locationAlias && (
                              <span style={{ color: T.mid, fontWeight: "normal" }}> · {o.locationAlias}</span>
                            )}
                          </td>

                          {marketsEnabled && <td style={{ ...TD, padding: isCompact ? "7px 14px" : "10px 14px" }}>
                            <span style={{ color: T.mid, fontSize: T.fsXxs }}>{o.region}</span>
                          </td>}

                          <td style={{ ...TD, textAlign: "right", padding: isCompact ? "7px 14px" : "10px 14px" }}>
                            {o.sf != null ? fmtSqft(o.sf) : <span style={{ color: T.mid }}>—</span>}
                          </td>

                          <td style={{ ...TD, textAlign: "right", padding: isCompact ? "7px 14px" : "10px 14px" }}>
                            {o.seats != null ? o.seats.toLocaleString() : <span style={{ color: T.mid }}>—</span>}
                          </td>

                          <td style={{ ...TD, textAlign: "right", padding: isCompact ? "7px 14px" : "10px 14px" }}>
                            {o.people != null ? o.people.toLocaleString() : <span style={{ color: T.mid }}>—</span>}
                          </td>

                          {!isCompact && (
                            <td style={{ ...TD, textAlign: "right", padding: "10px 14px" }}>
                              <DeskFitCell people={o.people} seats={o.seats} deskDelta={o.deskDelta} />
                            </td>
                          )}

                          {!isCompact && (
                            <td style={{ ...TD, textAlign: "right", padding: "10px 14px" }}>
                              {o.headroom != null
                                ? o.headroom.toLocaleString()
                                : <span style={{ color: T.mid }}>—</span>}
                            </td>
                          )}

                          {!isCompact && (
                            <td style={{ ...TD, padding: "10px 14px" }}>
                              <SpacePerSeatCell sf={o.sf} seats={o.seats} densityBand={o.densityBand} />
                            </td>
                          )}

                          <td style={{ ...TD, padding: isCompact ? "7px 14px" : "10px 14px" }}>
                            <VerdictPill verdict={o.verdict} />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {sorted.length > 0 && (
              <div style={{ fontSize: T.fsXs, color: T.mid }}>
                Sorted by verdict severity (Over-Capacity → Dense → Under-Used → Right-Sized), then by size.
                &ldquo;Desk fit&rdquo; ± = seats − assigned people.
              </div>
            )}
          </>
        )}

      </div>
    </ReportShell>
  );
}

// ─── Shared cell styles ───────────────────────────────────────────────────────

const TH = {
  padding:       "10px 14px",
  fontSize:      T.fsXxs,
  letterSpacing: ".07em",
  textTransform: "uppercase",
  color:         "var(--v2-mid)",
  fontWeight:    T.fwBold,
  borderBottom:  "1px solid var(--v2-line)",
  whiteSpace:    "nowrap",
  position:      "sticky",
  top:           0,
  zIndex:        3,
  background:    "var(--v2-tint)",
};

const TD = {
  verticalAlign: "middle",
  fontSize:      T.fsSm5,
  color:         T.dark,
};
